import { io, type Socket } from 'socket.io-client';
import { tokenStorage } from '@/services/tokenStorage';
import { API_BASE_URL } from '@/constants/api';

/** Live ALB routes /socket.io* → api-admin; /task-socket.io* is not on ALB. */
export const TASK_SOCKET_PATH = '/socket.io';
export const TASK_SOCKET_EVENT = 'task:changed' as const;
export const USER_AVATAR_SOCKET_EVENT = 'user:avatar' as const;

export type TaskRealtimeEventType =
  | 'task.created'
  | 'task.updated'
  | 'task.commented'
  | 'task.deleted';

export interface TaskRealtimeEvent {
  type: TaskRealtimeEventType;
  taskId: string;
  actorId?: string;
  at: string;
}

export interface UserAvatarRealtimeEvent {
  userId: string;
  employeeRef?: string;
  avatarUrl: string;
  at: string;
}

type TaskChangedHandler = (event: TaskRealtimeEvent) => void;
type AvatarHandler = (event: UserAvatarRealtimeEvent) => void;

/**
 * HMR / duplicate module instances must share one client + subscriber sets
 * so listeners survive reconnect and StrictMode remounts.
 */
const SOCKET_KEY = '__cgTaskSocket';
const TASK_HANDLERS_KEY = '__cgTaskChangedHandlers';
const AVATAR_HANDLERS_KEY = '__cgAvatarHandlers';
const DISPATCH_FLAG = '__cgTaskDispatchBound';

function getSocket(): Socket | null {
  if (typeof window === 'undefined') return null;
  return (window as unknown as Record<string, Socket | null>)[SOCKET_KEY] ?? null;
}

function setSocket(next: Socket | null): void {
  if (typeof window === 'undefined') return;
  (window as unknown as Record<string, Socket | null>)[SOCKET_KEY] = next;
}

function taskHandlers(): Set<TaskChangedHandler> {
  const w = window as unknown as Record<string, Set<TaskChangedHandler>>;
  if (!w[TASK_HANDLERS_KEY]) w[TASK_HANDLERS_KEY] = new Set();
  return w[TASK_HANDLERS_KEY];
}

function avatarHandlers(): Set<AvatarHandler> {
  const w = window as unknown as Record<string, Set<AvatarHandler>>;
  if (!w[AVATAR_HANDLERS_KEY]) w[AVATAR_HANDLERS_KEY] = new Set();
  return w[AVATAR_HANDLERS_KEY];
}

function bindDispatch(socket: Socket): void {
  const flagged = socket as Socket & { [DISPATCH_FLAG]?: boolean };
  if (flagged[DISPATCH_FLAG]) return;
  flagged[DISPATCH_FLAG] = true;

  socket.on(TASK_SOCKET_EVENT, (event: TaskRealtimeEvent) => {
    for (const handler of taskHandlers()) {
      try {
        handler(event);
      } catch {
        // Keep other subscribers alive.
      }
    }
  });

  socket.on(USER_AVATAR_SOCKET_EVENT, (event: UserAvatarRealtimeEvent) => {
    for (const handler of avatarHandlers()) {
      try {
        handler(event);
      } catch {
        // Keep other subscribers alive.
      }
    }
  });

  socket.on('connect', () => {
    // eslint-disable-next-line no-console
    console.info(
      '[task-socket] connected',
      socket.id,
      'transport=',
      socket.io.engine.transport.name,
    );
  });

  socket.on('connect_error', (err) => {
    // eslint-disable-next-line no-console
    console.warn('[task-socket] connect_error', err.message);
    const nextToken = tokenStorage.getAccessToken();
    if (nextToken) socket.auth = { token: nextToken };
  });
}

export function getTaskSocket(): Socket | null {
  return getSocket();
}

export function isTaskSocketConnected(): boolean {
  return Boolean(getSocket()?.connected);
}

/**
 * Same-origin URL, auth token, websocket first (DevTools WS/101).
 * Subscribers are registered via subscribe* so reconnect never drops handlers.
 */
export function connectTaskSocket(): Socket | null {
  const token = tokenStorage.getAccessToken();
  if (!token) {
    disconnectTaskSocket();
    return null;
  }

  const existing = getSocket();
  if (existing) {
    bindDispatch(existing);
    const currentToken = (existing.auth as Record<string, unknown> | undefined)?.token;
    if (token && currentToken !== token) {
      existing.auth = { token };
      if (existing.connected) existing.disconnect().connect();
      else existing.connect();
    } else if (!existing.connected) {
      existing.connect();
    }
    return existing;
  }

  // Claim the singleton slot immediately so parallel Auth + Bridge effects
  // cannot open a second Engine.IO connection.
  const socket = io(API_BASE_URL || undefined, {
    path: TASK_SOCKET_PATH,
    transports: ['websocket', 'polling'],
    auth: { token },
    autoConnect: true,
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 8000,
  });
  setSocket(socket);

  socket.io.on('reconnect_attempt', () => {
    const nextToken = tokenStorage.getAccessToken();
    if (nextToken) socket.auth = { token: nextToken };
  });

  bindDispatch(socket);
  return socket;
}

/** Stable subscribe — survives socket replace / StrictMode / HMR. */
export function subscribeTaskChanged(handler: TaskChangedHandler): () => void {
  taskHandlers().add(handler);
  connectTaskSocket();
  return () => {
    taskHandlers().delete(handler);
  };
}

export function subscribeUserAvatar(handler: AvatarHandler): () => void {
  avatarHandlers().add(handler);
  connectTaskSocket();
  return () => {
    avatarHandlers().delete(handler);
  };
}

export function refreshTaskSocketAuth(): void {
  const token = tokenStorage.getAccessToken();
  if (!token) {
    disconnectTaskSocket();
    return;
  }
  const existing = getSocket();
  if (!existing) {
    connectTaskSocket();
    return;
  }
  existing.auth = { token };
  existing.disconnect().connect();
}

export function emitUserAvatarChanged(payload: {
  userId: string;
  employeeRef?: string | null;
  avatarUrl: string;
}): void {
  const active = connectTaskSocket();
  if (!active) return;

  const body = {
    userId: payload.userId,
    employeeRef: payload.employeeRef ?? undefined,
    avatarUrl: payload.avatarUrl,
  };

  if (active.connected) {
    active.emit(USER_AVATAR_SOCKET_EVENT, body);
    return;
  }
  active.once('connect', () => {
    active.emit(USER_AVATAR_SOCKET_EVENT, body);
  });
}

export function disconnectTaskSocket(): void {
  const existing = getSocket();
  if (!existing) return;
  existing.off(TASK_SOCKET_EVENT);
  existing.off(USER_AVATAR_SOCKET_EVENT);
  existing.off('connect');
  existing.off('connect_error');
  existing.disconnect();
  setSocket(null);
}
